import { AxiosResponse } from 'axios';
import { useState } from 'react';

import { analytics } from 'analytics';
import { formToSankey } from 'utils/form';

import { useApiContext } from '../../contexts/apiContext';

// Extract filename from Content-Disposition header, falling back to a default name if not present
const getImageFilename = (response: AxiosResponse<File, any>) => {
  const contentDisposition = response.headers['content-disposition'];
  return /filename="(.+?)"/.exec(contentDisposition ?? '')?.[1];
};

/**
 * Hook to handle the downloading of an image based on the provided form data.
 *
 * @param form - The form data containing the image download settings.
 * @param onClose - Callback function to be called when the download is complete.
 *
 * @returns An object containing:
 * - `isDownloading`: A boolean indicating if the download is in progress.
 * - `downloadImage`: A function to initiate the image download process.
 */
export const useImageDownloader = (form: SankeyForm, onClose: () => void) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { api } = useApiContext();

  const imageFormat = form.watch('download_image_format') ?? 'png';
  const scale = Number(form.watch('download_scale_png') ?? 2);

  const downloadImage = async () => {
    setIsDownloading(true);
    try {
      // Call the API to download the image in the specified format.
      // The API will return a blob containing the image binary data.
      const sankey = formToSankey(form);
      const response = await api().utils.downloadImage(
        { downloadImageRequestRequest: { sankey }, imageFormat },
        { responseType: 'blob', params: { scale } }
      );
      // Create a temporary anchor element to trigger the download
      const link = document.createElement('a');
      link.href = URL.createObjectURL(response.data);
      link.download = getImageFilename(response) ?? `image.${imageFormat}`;
      link.click();
      // Revoke the object URL to free up memory and close the dialog
      URL.revokeObjectURL(link.href);
      onClose();
    } catch (error) {
      analytics.reportError(`Error downloading ${imageFormat} image`, {
        error,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return { isDownloading, downloadImage };
};
